import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getMembers, approveMember, suspendMember, reactivateMember } from '../../../services/members';
import DataTable from '../../../components/Tables/DataTable';
import Toast from '../../../components/Common/Toast';
import { 
  Users, Search, Eye, CheckCircle, ShieldAlert, UserPlus, AlertCircle, CreditCard
} from 'lucide-react';

const MemberList = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadMembers();
  }, []);

  const loadMembers = async () => {
    try {
      setLoading(true);
      const data = await getMembers();
      console.log('Raw members data:', data);
      setMembers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error loading members:', err);
      setToast({ type: 'error', message: 'Failed to load member list' });
    } finally {
      setLoading(false);
    }
  };

  const performAction = async (actionFn, successMsg, id) => {
    try {
      await actionFn(id);
      setToast({ type: 'success', message: successMsg });
      loadMembers();
    } catch (err) {
      setToast({ type: 'error', message: err.response?.data?.error || 'Action failed' });
    }
  };

  const getField = (member, field) => {
    if (!member) return null;
    if (member[field] !== undefined) return member[field];
    if (member.User && member.User[field] !== undefined) return member.User[field];
    if (member.user && member.user[field] !== undefined) return member.user[field];
    return null;
  };

  const getLatestPaymentStatus = (member) => {
    if (!member.RegistrationPayments || member.RegistrationPayments.length === 0) return null;
    return member.RegistrationPayments[0]?.status; // latest by created_at
  };

  const filteredMembers = members.filter(m => {
    const name = getField(m, 'full_name') || '';
    const memberId = m.member_number || '';
    return name.toLowerCase().includes(searchTerm.toLowerCase()) ||
           memberId.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const columns = [
    {
      key: 'member_number',
      label: 'ID / Member #',
      render: (_, row) => (
        <span className="font-mono text-xs font-black text-slate-500 bg-slate-100 px-2 py-1 rounded">
          {row?.member_number || 'UNASSIGNED'}
        </span>
      )
    },
    {
      key: 'full_name',
      label: 'Member Name',
      render: (_, row) => {
        const name = getField(row, 'full_name') || 'Unknown';
        const email = getField(row, 'email') || 'No email';
        return (
          <div className="flex flex-col">
            <span className="font-bold text-slate-900 leading-none">{name}</span>
            <span className="text-[11px] text-slate-400 mt-1 italic">{email}</span>
          </div>
        );
      }
    },
    {
      key: 'payment_status',
      label: 'Payment',
      render: (_, row) => {
        const paymentStatus = getLatestPaymentStatus(row);
        if (!paymentStatus) return <span className="text-slate-400 text-xs">Not started</span>;
        if (paymentStatus === 'paid') return <span className="text-emerald-600 text-xs font-bold">Paid</span>;
        if (paymentStatus === 'pending') return <span className="text-amber-600 text-xs font-bold">Pending</span>;
        return <span className="text-rose-600 text-xs">{paymentStatus}</span>;
      }
    },
    {
      key: 'status',
      label: 'Status',
      render: (_, row) => {
        const status = row?.status || 'unknown';
        const styles = {
          active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
          pending: 'bg-amber-50 text-amber-700 border-amber-200',
          suspended: 'bg-rose-50 text-rose-700 border-rose-200',
        };
        return (
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest border ${styles[status] || 'bg-slate-50 text-slate-500'}`}>
            <span className="h-1 w-1 rounded-full bg-current" />
            {status}
          </span>
        );
      }
    },
    {
      key: 'actions',
      label: 'Lifecycle Actions',
      render: (_, row) => {
        const paymentStatus = getLatestPaymentStatus(row);
        const isPaymentCompleted = paymentStatus === 'paid';

        return (
          <div className="flex items-center gap-2">
            <Link
              to={`/dashboard/org-admin/members/${row.id}`}
              className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all"
              title="View Profile"
            >
              <Eye size={18} />
            </Link>

            {row.status === 'pending' && isPaymentCompleted && (
              <button
                onClick={() => performAction(approveMember, 'Member approved', row.id)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 text-white text-[11px] font-bold rounded-lg hover:bg-emerald-700 transition-all shadow-sm active:scale-95"
              >
                <CheckCircle size={14} /> Approve
              </button>
            )}

            {row.status === 'pending' && !isPaymentCompleted && (
              <span className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 text-amber-600 text-[11px] font-bold rounded-lg">
                <CreditCard size={14} /> Awaiting payment
              </span>
            )}

            {row.status === 'active' && (
              <button
                onClick={() => performAction(suspendMember, 'Member suspended', row.id)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-white text-rose-600 border border-rose-100 text-[11px] font-bold rounded-lg hover:bg-rose-50 transition-all active:scale-95"
              >
                <ShieldAlert size={14} /> Suspend
              </button>
            )}

            {row.status === 'suspended' && (
              <button
                onClick={() => performAction(reactivateMember, 'Member reactivated', row.id)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-50 text-indigo-700 text-[11px] font-bold rounded-lg hover:bg-indigo-100 transition-all active:scale-95"
              >
                <UserPlus size={14} /> Reactivate
              </button>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto space-y-8 animate-in fade-in duration-500">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 border-b border-slate-200 pb-8">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Users size={18} className="text-indigo-600" />
            <span className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Membership Registry</span>
          </div>
          <h1 className="text-4xl font-black text-slate-900 tracking-tight">Organization Members</h1>
          <p className="text-slate-500 font-medium mt-1">Review registrations, manage approvals, and track member status.</p>
        </div>
      </div>

      <div className="max-w-md relative group">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-indigo-500 transition-colors" size={18} />
        <input
          type="text"
          placeholder="Search by name or member ID..."
          className="w-full pl-12 pr-4 py-3.5 bg-white border border-slate-200 rounded-2xl focus:ring-4 focus:ring-indigo-500/10 transition-all text-sm shadow-sm"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <div className="bg-white rounded-[2rem] border border-slate-200 shadow-xl shadow-slate-100/50 overflow-hidden min-h-[400px]">
        <DataTable columns={columns} data={filteredMembers} loading={loading} />
        
        {!loading && filteredMembers.length === 0 && (
          <div className="py-24 text-center">
            <AlertCircle size={40} className="mx-auto text-slate-200 mb-4" />
            <p className="text-slate-900 font-black italic">No members found</p>
            <p className="text-slate-400 text-sm">Refine your search or check the pending queue.</p>
          </div>
        )}
      </div>

      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
    </div>
  );
};

export default MemberList;